"use client";

import React, { useState, useEffect } from "react";
import { FiChevronRight } from "react-icons/fi";
import { WhiteStar } from "../rating-section/rating-section";
import { DateTime } from "luxon";
import { AffiliateLinkContainer } from "@monorepo-nx/std-watch/ui";
import { SendEvent } from '@monorepo-nx/std-watch/services/analytics';


interface ScheduleEntry {
  cutoff?: string; // e.g. "15:00"
  deliveryMadeAsLateAs?: string | null;
  note?: string; // e.g. "No delivery"
}

interface Props {
  promotion: any;
  score: number;
  senderTimeZone: string;
  recipientTimeZone: string;
  timeSection: ScheduleEntry;
}

const formatTime = (time24?: string): string => {
  if (!time24 || typeof time24 !== "string" || !time24.includes(":")) return "—";
  const [hour, minute] = time24.split(":").map(Number);
  const isPM = hour >= 12;
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute.toString().padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
};


const handleClick = (e, href, slug) => {
  SendEvent({
    eventName: 'click-component',
    component: 'same-day-delivery-card',
    value: slug,
    text: slug,
    href: href,
    section: 'same-day-delivery-card'
  });
}




const SameDayDeliveryCard: React.FC<Props> = ({
  promotion,
  score,
  senderTimeZone,
  recipientTimeZone,
  timeSection
}) => {
  const [timeLeft, setTimeLeft] = useState<string>("00:00:00");
  const deeplink_name = promotion.deepLink ? promotion.deepLink.name : promotion.advertiser.deepLink?.name
  const deeplink_affiliate_link = promotion.deepLink ? promotion.deepLink.affiliate_link : promotion.advertiser.deepLink?.affiliate_link
  const isDisabled = promotion.advertiser.isDisabled || false

  useEffect(() => {
    if (!timeSection?.cutoff) return;

    const [cutoffHour, cutoffMinute] = timeSection.cutoff.split(":").map(Number);

    // 1. Get current time in sender's timezone (e.g. Israel)
    const nowInSenderTZ = DateTime.now().setZone(senderTimeZone);

    // 2. Create cutoff time in recipient timezone (e.g. New York 3:00 PM)
    const cutoffInRecipientTZ = DateTime.now()
      .setZone(recipientTimeZone)
      .set({ hour: cutoffHour, minute: cutoffMinute, second: 0, millisecond: 0 });

    // 3. Convert recipient cutoff to UTC
    const cutoffUTC = cutoffInRecipientTZ.toUTC();

    // 4. Convert sender time to UTC
    const senderUTC = nowInSenderTZ.toUTC();

    // 5. Get diff
    const diff = cutoffUTC.diff(senderUTC, ["hours", "minutes", "seconds"]).toObject();

    if ((diff.hours ?? 0) <= 0 && (diff.minutes ?? 0) <= 0) {
      setTimeLeft("00:00:00");
      return;
    }

    const h = Math.floor(diff.hours ?? 0);
    const m = Math.floor(diff.minutes ?? 0);
    const s = Math.floor(diff.seconds ?? 0);

    setTimeLeft(
      `${String(h).padStart(1, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    );
  }, [senderTimeZone, recipientTimeZone, timeSection]);



  const [hoursStr, minsStr, secsStr] = timeLeft.split(":");

  return (
    <div className="w-full max-w-sm md:max-w-5xl mx-auto bg-white rounded-2xl shadow-lg px-6 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
      {/* Left: Advertiser name + rating */}
      <div className="w-full md:basis-[20%]">
        <div className="flex flex-row md:flex-col items-center md:items-start justify-between md:justify-start gap-1">
          {/* Advertiser Name */}
          <h2 className="text-lg md:text-xl font-semibold font-figtree truncate m-0">
            {promotion.advertiser.name}
          </h2>

          {/* Score + Star */}
          <div className="flex items-center text-xs font-bold font-dmsans mt-0 md:pt-1">
            <div className="inline-flex items-center justify-center w-4 h-4 bg-amber-500 text-white rounded-sm mr-1">
              <WhiteStar />
            </div>
            <span>{score}</span>
          </div>
        </div>
      </div>




      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        {/* Countdown Section */}
        {timeSection?.cutoff && (
          <div className="w-full md:basis-[30%] order-2 md:order-2 mt-0 md:mt-3 md:mt-0">
            <p className="text-sm md:text-xs text-gray-500 font-dmsans font-bold mb-2">
              ⏰ Time left
            </p>

            <div className="text-2xl md:text-2xl font-semibold font-figtree inline-flex items-baseline">
              <span>{hoursStr}</span>
              <span className="text-gray-300 font-semibold ml-1 md:text-xl text-xl md:text-base">
                hrs
              </span>
            </div>
          </div>
        )}



        {/* Schedule Info Section */}
        <div className="w-full md:basis-[60%] order-1 md:order-1 text-sm text-gray-500 font-dmsans mt-3 md:mt-0">
          {timeSection ? (
            "note" in timeSection ? (
              <p className="m-0 text-gray-400">{timeSection.note}</p>
            ) : (
              <p className="m-0 md:mt-5 text-gray-500">
                Order today by <strong>{formatTime(timeSection.cutoff)}</strong><br />
                to have package delivered by{" "}
                {timeSection.deliveryMadeAsLateAs
                  ? formatTime(timeSection.deliveryMadeAsLateAs)
                  : "end of day"}
              </p>
            )
          ) : (
            <p className="m-0 text-gray-400">No delivery info available</p>
          )}
        </div>
      </div>


      {/* Action Button */}
      <div className="w-full md:w-auto mt-4 md:mt-0 md:basis-[10%]" onClick={(e) => handleClick(e, deeplink_name, promotion.slug)}>
        <AffiliateLinkContainer affName={deeplink_name} affLink={deeplink_affiliate_link} isDisabled={isDisabled}>
          <button className="w-full md:hidden bg-rose-500 text-white text-center py-3 rounded-2xl font-semibold text-sm">
            Visit Site
          </button>

          <button className="hidden md:flex bg-rose-500 text-white rounded-full w-10 h-10 items-center justify-center transition-transform duration-300 hover:scale-110 group">
            <FiChevronRight className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </AffiliateLinkContainer>
      </div>

    </div>
  );
};

export default SameDayDeliveryCard;
